<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  email,
  minLength,
  sameAs,
  helpers,
} from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AgentsAPI from 'dashboard/api/agents';

const emit = defineEmits(['close', 'success']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');

// 密码相关字段
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);

const agentUsage = ref({
  current_count: 0,
  max_limit: 100000,
  available_slots: 100000,
});

// 自定义密码复杂度验证器
const containsUppercase = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PASSWORD.DETAILS.UPPERCASE'),
  value => !helpers.req(value) || /[A-Z]/.test(value)
);
const containsLowercase = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PASSWORD.DETAILS.LOWERCASE'),
  value => !helpers.req(value) || /[a-z]/.test(value)
);
const containsNumber = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PASSWORD.DETAILS.NUMBER'),
  value => !helpers.req(value) || /[0-9]/.test(value)
);
const containsSpecial = helpers.withMessage(
  () => t('AGENT_MGMT.ADD.FORM.PASSWORD.DETAILS.SPECIAL'),
  value => !helpers.req(value) || /[!@#$%^&*()_+\-=\[\]{}|']/.test(value)
);

onMounted(async () => {
  try {
    const { data } = await AgentsAPI.getUsageInfo();
    agentUsage.value = data;
  } catch (error) {
    // Fall back to restrictive defaults (safe mode) - prevents adding agents if API fails
    agentUsage.value = {
      current_count: 0,
      max_limit: 0,
      available_slots: 0,
    };
  }
});

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
  agentPassword: {
    required,
    minLength: minLength(8),
    containsUppercase,
    containsLowercase,
    containsNumber,
    containsSpecial,
  },
  agentPasswordConfirmation: {
    required,
    sameAsPassword: sameAs(agentPassword),
  },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
  agentPassword,
  agentPasswordConfirmation,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// 密码强度计算
const passwordStrength = computed(() => {
  const password = agentPassword.value;
  if (!password) return { level: 0, label: '', colorClass: '' };

  let level = 0;
  if (password.length >= 8) level++;
  if (password.length >= 12) level++;
  if (/[A-Z]/.test(password)) level++;
  if (/[0-9]/.test(password)) level++;
  if (/[^A-Za-z0-9]/.test(password)) level++;

  if (level <= 2) {
    return {
      level,
      label: t('AGENT_MGMT.ADD.FORM.PASSWORD.STRENGTH.WEAK'),
      colorClass: 'bg-n-ruby-8',
    };
  }
  if (level <= 3) {
    return {
      level,
      label: t('AGENT_MGMT.ADD.FORM.PASSWORD.STRENGTH.MEDIUM'),
      colorClass: 'bg-n-amber-8',
    };
  }
  return {
    level,
    label: t('AGENT_MGMT.ADD.FORM.PASSWORD.STRENGTH.STRONG'),
    colorClass: 'bg-n-teal-8',
  };
});

// 切换密码可见性
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const togglePasswordConfirmationVisibility = () => {
  showPasswordConfirmation.value = !showPasswordConfirmation.value;
};

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
      password: agentPassword.value,
      password_confirmation: agentPasswordConfirmation.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    emit('success');
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const { response } = error;
    const { data: errorData = {} } = response || {};

    // 处理限制错误
    if (response?.status === 402) {
      useAlert(
        t('AGENT_MGMT.ADD.API.LIMIT_ERROR', {
          current: errorData.current_count || agentUsage.value.current_count,
          max: errorData.max_limit || agentUsage.value.max_limit,
        })
      );
    } else if (
      response?.status === 422 &&
      !errorData.attributes?.includes('base')
    ) {
      useAlert(t('AGENT_MGMT.ADD.API.EXIST_MESSAGE'));
    } else {
      useAlert(errorData.error || t('AGENT_MGMT.ADD.API.ERROR_MESSAGE'));
    }
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />

    <!-- 使用信息横幅 -->
    <div
      v-if="agentUsage.max_limit < 100000"
      class="usage-banner mx-4 mb-4 p-3 rounded-lg border text-sm"
      :class="{
        'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300':
          agentUsage.available_slots <= 3 && agentUsage.available_slots > 0,
        'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300':
          agentUsage.available_slots === 0,
        'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300':
          agentUsage.available_slots > 3,
      }"
    >
      {{
        $t('AGENT_MGMT.USAGE_INFO', {
          current: agentUsage.current_count,
          max: agentUsage.max_limit,
          available: agentUsage.available_slots,
        })
      }}
    </div>

    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <!-- 密码输入框 -->
      <div class="w-full relative">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <div class="relative">
            <input
              v-model="agentPassword"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
              class="w-full pr-10"
              @input="v$.agentPassword.$touch"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 hover:text-n-slate-12"
              @click="togglePasswordVisibility"
            >
              <Icon
                :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                size="16"
              />
            </button>
          </div>
          <!-- 密码验证错误提示 -->
          <div v-if="v$.agentPassword.$error" class="flex flex-col gap-1 mt-1">
            <span
              v-if="v$.agentPassword.minLength.$invalid"
              class="message text-xs"
            >
              密码至少需要 8 位
            </span>
            <span
              v-if="v$.agentPassword.containsUppercase.$invalid"
              class="message text-xs"
            >
              {{ v$.agentPassword.containsUppercase.$message }}
            </span>
            <span
              v-if="v$.agentPassword.containsLowercase.$invalid"
              class="message text-xs"
            >
              {{ v$.agentPassword.containsLowercase.$message }}
            </span>
            <span
              v-if="v$.agentPassword.containsNumber.$invalid"
              class="message text-xs"
            >
              {{ v$.agentPassword.containsNumber.$message }}
            </span>
            <span
              v-if="v$.agentPassword.containsSpecial.$invalid"
              class="message text-xs"
            >
              {{ v$.agentPassword.containsSpecial.$message }}
            </span>
          </div>
          <!-- 密码规则提示 -->
          <div v-else class="text-xs text-n-slate-11 dark:text-n-slate-7 mt-1">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.REQUIREMENTS') }}
          </div>
        </label>

        <!-- 密码强度指示器 -->
        <div
          v-if="agentPassword && passwordStrength.level > 0"
          class="mt-2 flex items-center gap-2"
        >
          <div class="flex-1 h-1.5 bg-n-slate-3 rounded-full overflow-hidden">
            <div
              class="h-full transition-all duration-300"
              :class="passwordStrength.colorClass"
              :style="{ width: `${(passwordStrength.level / 5) * 100}%` }"
            />
          </div>
          <span
            class="text-xs"
            :class="`text-${passwordStrength.colorClass.replace('bg-', 'text-')}`"
          >
            {{ passwordStrength.label }}
          </span>
        </div>
      </div>

      <!-- 确认密码输入框 -->
      <div class="w-full relative">
        <label :class="{ error: v$.agentPasswordConfirmation.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.LABEL') }}
          <div class="relative">
            <input
              v-model="agentPasswordConfirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              :placeholder="
                $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
              "
              class="w-full pr-10"
              @input="v$.agentPasswordConfirmation.$touch"
            />
            <button
              type="button"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 hover:text-n-slate-12"
              @click="togglePasswordConfirmationVisibility"
            >
              <Icon
                :icon="
                  showPasswordConfirmation ? 'i-lucide-eye-off' : 'i-lucide-eye'
                "
                size="16"
              />
            </button>
          </div>
          <span v-if="v$.agentPasswordConfirmation.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="
            v$.$invalid ||
            uiFlags.isCreating ||
            agentUsage.available_slots === 0
          "
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
